import {
  ChangeEvent,
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { api, RequestError } from "../lib/api";
import { invitationPhones, invitationText, invitationUrl, reminderText, smsHref } from "../lib/invitationShare";
import { useAnimatedClose } from "../hooks/useAnimatedClose";

interface Summary {
  invitations: number;
  responses: number;
  pending: number;
  attending_households: number;
  declined_households: number;
  attendees: number;
  adults: number;
  children: number;
  jeffisms: number;
  pending_capacity: number;
}
interface Invitation {
  id: number;
  token: string;
  name: string;
  household_name: string | null;
  email: string | null;
  phone: string | null;
  backup_phone: string | null;
  max_party_size: number | null;
  notes: string | null;
  submitted_at: string | null;
  rsvp_status: "yes" | "no" | null;
  attendee_count: number | null;
  adult_count: number | null;
  child_count: number | null;
  submitted_name: string | null;
  submitted_email: string | null;
  submitted_phone: string | null;
  rsvp_note: string | null;
  jeffism_text: string | null;
  jeffism_credit: string | null;
  jeffism_anonymous: number;
  jeffism_favorite: number;
  admin_note: string | null;
  last_texted_at: string | null;
}
interface ImportRow {
  rowNumber: number;
  name: string;
  householdName: string | null;
  email: string | null;
  phone: string | null;
  backupPhone: string | null;
  maxPartySize: number | null;
  errors: string[];
  duplicate: boolean;
}
interface ContactMessage {
  id: number;
  name: string;
  email: string;
  message: string;
  created_at: string;
  read_at: string | null;
}
type Tab = "dashboard" | "send" | "import" | "jeffisms" | "messages";

function downloadCsv(filename: string, rows: Record<string, unknown>[]) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const cell = (value: unknown) => {
    const text = value == null ? "" : String(value);
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };
  const csv = [
    headers.join(","),
    ...rows.map((row) => headers.map((key) => cell(row[key])).join(",")),
  ].join("\r\n");
  const link = document.createElement("a");
  link.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

export function AdminApp() {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [tab, setTab] = useState<Tab>("dashboard");
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Invitation | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [buffer, setBuffer] = useState(() =>
    Number(localStorage.getItem("food-buffer") || 10),
  );
  const load = useCallback(async () => {
    setError("");
    setLoading(true);
    try {
      const query = new URLSearchParams();
      if (tab === "send") query.set("status", "pending");
      else if (filter !== "all") query.set("status", filter);
      if (search) query.set("search", search);
      const [summaryBody, listBody] = await Promise.all([
        api<{ summary: Summary }>("/admin/api/summary"),
        api<{ invitations: Invitation[] }>(`/admin/api/invitations?${query}`),
      ]);
      setSummary(summaryBody.summary);
      setInvitations(listBody.invitations);
    } catch (caught) {
      setError(
        caught instanceof RequestError && caught.status === 403
          ? "Cloudflare Access did not authorize this account. Confirm the Access policy and ADMIN_EMAILS setting."
          : caught instanceof Error
            ? caught.message
            : "Could not load the dashboard.",
      );
    } finally {
      setLoading(false);
    }
  }, [filter, search, tab]);
  useEffect(() => {
    const timer = setTimeout(load, 180);
    return () => clearTimeout(timer);
  }, [load]);
  const loadMessages = useCallback(async () => {
    try {
      const body = await api<{ messages: ContactMessage[] }>(
        "/admin/api/messages",
      );
      setMessages(body.messages);
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Could not load messages.",
      );
    }
  }, []);
  const responseRate = summary?.invitations
    ? Math.round((summary.responses / summary.invitations) * 100)
    : 0;
  const foodCount = summary
    ? Math.ceil(summary.attendees * (1 + buffer / 100))
    : 0;
  const jeffisms = useMemo(
    () => invitations.filter((invite) => invite.jeffism_text),
    [invitations],
  );
  return (
    <main className="admin-shell">
      <aside className="admin-sidebar">
        <a href="/" className="admin-brand">
          JEFF'S <span>70TH</span>
          <small>Party HQ</small>
        </a>
        <nav aria-label="Admin sections">
          <button
            className={tab === "dashboard" ? "active" : ""}
            onClick={() => {
              setTab("dashboard");
              setFilter("all");
            }}
          >
            <i className="fa-solid fa-chart-simple" aria-hidden="true" />{" "}
            Dashboard
          </button>
          <button
            className={tab === "send" ? "active" : ""}
            onClick={() => {
              setTab("send");
              setFilter("pending");
              setSearch("");
            }}
          >
            <i className="fa-solid fa-comment-sms" aria-hidden="true" /> Text
            invites
          </button>
          <button
            className={tab === "import" ? "active" : ""}
            onClick={() => setTab("import")}
          >
            <i className="fa-solid fa-file-import" aria-hidden="true" /> Import
            invites
          </button>
          <button
            className={tab === "jeffisms" ? "active" : ""}
            onClick={() => {
              setTab("jeffisms");
              setFilter("jeffism");
            }}
          >
            <i className="fa-solid fa-quote-left" aria-hidden="true" /> Jeffisms
          </button>
          <button
            className={tab === "messages" ? "active" : ""}
            onClick={() => {
              setTab("messages");
              loadMessages();
            }}
          >
            <i className="fa-regular fa-envelope" aria-hidden="true" /> Messages
          </button>
        </nav>
        <a className="view-site" href="/" target="_blank">
          View event site ↗
        </a>
      </aside>
      <section className="admin-content">
        <header className="admin-header">
          <div>
            <p className="eyebrow">Jeff's 70th</p>
            <h1>
              {tab === "dashboard"
                ? "Headcount & RSVPs"
                : tab === "send"
                  ? "Text invitation queue"
                : tab === "import"
                  ? "Import invitations"
                  : tab === "messages"
                    ? "Organizer messages"
                    : "Jeffism archives"}
            </h1>
          </div>
          {tab === "dashboard" && (
            <div className="export-menu">
              <button className="admin-button" onClick={() => setCreating(true)}>
                <i className="fa-solid fa-plus" aria-hidden="true" /> Add invitation
              </button>
              <a href="/admin/api/export/rsvps.csv">
                <i className="fa-solid fa-download" aria-hidden="true" /> Export
                RSVPs
              </a>
              <a href="/admin/api/export/attendees.csv">
                <i className="fa-solid fa-table-list" aria-hidden="true" />{" "}
                Attendee sheet
              </a>
            </div>
          )}
        </header>
        {error && (
          <div className="admin-error" role="alert">
            {error}
          </div>
        )}
        {tab === "dashboard" && (
          <>
            {summary && (
              <>
                <div className="summary-grid">
                  <SummaryCard
                    label="Invitations"
                    value={summary.invitations}
                    detail={`${responseRate}% responded`}
                  />
                  <SummaryCard
                    label="Responses"
                    value={summary.responses}
                    detail={`${summary.pending} pending`}
                  />
                  <SummaryCard
                    label="Attending"
                    value={summary.attending_households}
                    detail={`${summary.declined_households} declined`}
                  />
                  <SummaryCard
                    label="Confirmed people"
                    value={summary.attendees}
                    detail={`${summary.adults} adults · ${summary.children} kids`}
                  />
                  <SummaryCard
                    label="Possible range"
                    value={`${summary.attendees}–${summary.attendees + summary.pending_capacity}`}
                    detail={`${summary.pending_capacity} pending capacity`}
                  />
                  <div className="summary-card planning">
                    <span>Food planning</span>
                    <strong>{foodCount}</strong>
                    <label>
                      Confirmed +{" "}
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={buffer}
                        onChange={(e) => {
                          const value = Number(e.target.value);
                          setBuffer(value);
                          localStorage.setItem("food-buffer", String(value));
                        }}
                      />
                      %
                    </label>
                  </div>
                </div>
                <div className="response-meter">
                  <i style={{ width: `${responseRate}%` }} />
                  <span>{responseRate}% response rate</span>
                </div>
              </>
            )}
            <section className="admin-panel">
              <div className="table-tools">
                <div className="filters">
                  {[
                    ["all", "All"],
                    ["yes", "Attending"],
                    ["no", "Declined"],
                    ["pending", "Pending"],
                    ["jeffism", "Has Jeffism"],
                  ].map(([value, label]) => (
                    <button
                      key={value}
                      className={filter === value ? "active" : ""}
                      onClick={() => setFilter(value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <label className="search">
                  <span className="sr-only">Search invitations</span>
                  <input
                    type="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search name, email, phone…"
                  />
                </label>
              </div>
              <InvitationTable
                invitations={invitations}
                loading={loading}
                onSelect={setSelected}
              />
            </section>
          </>
        )}
        {tab === "import" && (
          <ImportPanel
            onComplete={() => {
              setTab("dashboard");
              load();
            }}
          />
        )}
        {tab === "send" && (
          <SendInvitesPanel
            invitations={invitations}
            search={search}
            onSearch={setSearch}
            onTextOpened={(id) => {
              const openedAt = new Date().toISOString();
              setInvitations((current) => current.map((invite) => invite.id === id ? { ...invite, last_texted_at: openedAt } : invite));
            }}
          />
        )}
        {tab === "jeffisms" && (
          <JeffismPanel invitations={jeffisms} onSelect={setSelected} />
        )}
        {tab === "messages" && (
          <MessagesPanel messages={messages} onRefresh={loadMessages} />
        )}
      </section>
      {creating && (
        <CreateInvitation
          onClose={() => setCreating(false)}
          onCreated={(invitation) => {
            setCreating(false);
            setSelected(invitation);
            load();
          }}
        />
      )}
      {selected && (
        <EditInvitation
          invitation={selected}
          onClose={() => setSelected(null)}
          onSaved={() => {
            setSelected(null);
            load();
          }}
        />
      )}
    </main>
  );
}

function CreateInvitation({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (invitation: Invitation) => void;
}) {
  const [form, setForm] = useState({
    name: "",
    household_name: "",
    email: "",
    phone: "",
    backup_phone: "",
    max_party_size: 6,
    notes: "",
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { closing, requestClose } = useAnimatedClose(onClose);
  const set = (key: string, value: string | number) =>
    setForm((current) => ({ ...current, [key]: value }));
  const create = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const result = await api<{ invitation: Invitation }>("/admin/api/invitations", {
        method: "POST",
        body: JSON.stringify(form),
      });
      requestClose(() => onCreated(result.invitation));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Invitation creation failed.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      className={`modal-backdrop edit-backdrop ${closing ? "is-closing" : ""}`}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) requestClose();
      }}
    >
      <section className="edit-panel create-invitation-panel" role="dialog" aria-modal="true" aria-label="Add invitation">
        <button className="icon-button" onClick={() => requestClose()}>×</button>
        <p className="eyebrow">New guest or household</p>
        <h2>Add invitation</h2>
        <form onSubmit={create}>
          <div className="edit-grid">
            <label>
              Name
              <input required autoFocus maxLength={120} value={form.name} onChange={(e) => set("name", e.target.value)} />
            </label>
            <label>
              Household
              <input maxLength={120} value={form.household_name} onChange={(e) => set("household_name", e.target.value)} />
            </label>
            <label>
              Maximum party size
              <input type="number" min="1" max="20" required value={form.max_party_size} onChange={(e) => set("max_party_size", Number(e.target.value))} />
            </label>
            <label>
              Email
              <input type="email" maxLength={254} value={form.email} onChange={(e) => set("email", e.target.value)} />
            </label>
            <label>
              Mobile phone
              <input maxLength={40} value={form.phone} onChange={(e) => set("phone", e.target.value)} />
            </label>
            <label>
              Backup mobile
              <input maxLength={40} value={form.backup_phone} onChange={(e) => set("backup_phone", e.target.value)} />
            </label>
          </div>
          <label>
            Notes
            <textarea rows={3} maxLength={1000} value={form.notes} onChange={(e) => set("notes", e.target.value)} />
          </label>
          <p className="soft-message">A unique RSVP link will be generated automatically.</p>
          {error && <p className="admin-error" role="alert">{error}</p>}
          <div className="edit-actions">
            <button type="button" className="danger-link" onClick={() => requestClose()} disabled={busy}>Cancel</button>
            <button className="admin-button" disabled={busy}>{busy ? "Creating…" : "Create invitation"}</button>
          </div>
        </form>
      </section>
    </div>
  );
}

function SendInvitesPanel({
  invitations,
  search,
  onSearch,
  onTextOpened,
}: {
  invitations: Invitation[];
  search: string;
  onSearch: (value: string) => void;
  onTextOpened: (id: number) => void;
}) {
  const pendingInvitations = invitations.filter((invite) => invite.submitted_at === null && invite.rsvp_status === null);
  const textable = pendingInvitations
    .filter((invite) => invitationPhones(invite).length > 0)
    .sort((left, right) => Number(Boolean(left.last_texted_at)) - Number(Boolean(right.last_texted_at)) || left.name.localeCompare(right.name));
  const missingPhone = pendingInvitations.length - textable.length;
  const opened = textable.filter((invite) => invite.last_texted_at).length;

  return (
    <section className="text-queue">
      <div className="text-queue-intro">
        <div>
          <p className="eyebrow">Mobile send mode</p>
          <h2>{textable.length - opened} new · {opened} reminder-ready</h2>
          <p>Send the first invitation, then gently ping anyone who is still pending. Each message opens with the recipient and personal RSVP link already filled in.</p>
        </div>
        <div className="text-queue-counts" aria-label="Text invitation progress">
          <strong>{opened}</strong><span>reminder-ready</span>
          <strong>{missingPhone}</strong><span>missing phone</span>
        </div>
      </div>
      <label className="text-queue-search">
        <span className="sr-only">Search text invitation queue</span>
        <input type="search" value={search} onChange={(event) => onSearch(event.target.value)} placeholder="Search name, household, or phone…" />
      </label>
      <div className="text-invite-grid">
        {textable.map((invite) => (
          <TextInviteCard invitation={invite} key={invite.id} onTextOpened={onTextOpened} />
        ))}
      </div>
      {!textable.length && <p className="empty-table">No pending invitations with phone numbers match this view.</p>}
      {missingPhone > 0 && <p className="text-queue-note"><i className="fa-solid fa-circle-info" aria-hidden="true" /> {missingPhone} pending {missingPhone === 1 ? "invitation has" : "invitations have"} no phone number. Add one from the dashboard or send the link another way.</p>}
    </section>
  );
}

function TextInviteCard({ invitation, onTextOpened }: { invitation: Invitation; onTextOpened: (id: number) => void }) {
  const [copied, setCopied] = useState(false);
  const isReminder = Boolean(invitation.last_texted_at);
  const message = isReminder ? reminderText(invitation) : invitationText(invitation);
  const url = invitationUrl(invitation.token);
  const phones = invitationPhones(invitation);
  const markOpened = () => {
    onTextOpened(invitation.id);
    void fetch(`/admin/api/invitations/${invitation.id}/text-opened`, { method: "POST", keepalive: true });
  };
  const copy = async () => {
    await navigator.clipboard.writeText(message);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  return (
    <article className={`text-invite-card ${isReminder ? "reminder-ready" : ""}`}>
      <header>
        <div>
          <h3>{invitation.name}</h3>
          <p>{invitation.household_name || "Individual invitation"}</p>
        </div>
        {isReminder && <span className="text-opened"><i className="fa-solid fa-bell" aria-hidden="true" /> Reminder ready</span>}
      </header>
      <p className="text-message-kind">{isReminder ? "Gentle RSVP reminder" : "First invitation"}</p>
      <p className="text-message-preview">{message}</p>
      <div className="text-recipient-list">
        {phones.map(({ label, phone }) => (
          <div className="text-recipient" key={`${label}-${phone}`}>
            <a className="text-phone" href={`tel:${phone}`}><small>{label}</small>{phone}</a>
            <a className="text-send-button" href={smsHref(phone, message)} onClick={markOpened}>
              <i className={`fa-solid ${isReminder ? "fa-bell" : "fa-comment-sms"}`} aria-hidden="true" /> {isReminder ? "Remind" : "Text"} {label.toLowerCase()}
            </a>
          </div>
        ))}
      </div>
      <button className="text-copy-button" type="button" onClick={copy} aria-label={`Copy ${isReminder ? "reminder" : "invitation"} message for ${invitation.name}`}>
        <i className={`fa-solid ${copied ? "fa-check" : "fa-copy"}`} aria-hidden="true" /> {copied ? "Copied" : isReminder ? "Copy reminder" : "Copy message"}
      </button>
      <code>{url}</code>
    </article>
  );
}

function MessagesPanel({
  messages,
  onRefresh,
}: {
  messages: ContactMessage[];
  onRefresh: () => void;
}) {
  const toggle = async (item: ContactMessage) => {
    await api(`/admin/api/messages/${item.id}`, {
      method: "PATCH",
      body: JSON.stringify({ read: !item.read_at }),
    });
    onRefresh();
  };
  if (!messages.length)
    return <p className="empty-table">No organizer messages yet.</p>;
  return (
    <section className="message-list">
      {messages.map((item) => (
        <article
          className={`message-card ${item.read_at ? "" : "unread"}`}
          key={item.id}
        >
          <header>
            <h2>{item.name}</h2>
            <a href={`mailto:${item.email}`}>{item.email}</a>
          </header>
          <p>{item.message}</p>
          <footer>
            <time dateTime={item.created_at}>
              {new Date(item.created_at).toLocaleString()}
            </time>
            <button onClick={() => toggle(item)}>
              {item.read_at ? "Mark unread" : "Mark read"}
            </button>
          </footer>
        </article>
      ))}
    </section>
  );
}

function SummaryCard({
  label,
  value,
  detail,
}: {
  label: string;
  value: string | number;
  detail: string;
}) {
  return (
    <div className="summary-card">
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{detail}</small>
    </div>
  );
}

function InvitationTable({
  invitations,
  loading,
  onSelect,
}: {
  invitations: Invitation[];
  loading: boolean;
  onSelect: (invite: Invitation) => void;
}) {
  if (loading) return <p className="empty-table">Loading invitations…</p>;
  if (!invitations.length)
    return <p className="empty-table">No invitations match this view.</p>;
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Contact</th>
            <th>Status</th>
            <th>Party</th>
            <th>Adults</th>
            <th>Kids</th>
            <th>Jeffism</th>
            <th>Submitted</th>
            <th>Invite</th>
          </tr>
        </thead>
        <tbody>
          {invitations.map((invite) => (
            <tr
              key={invite.id}
              onClick={() => onSelect(invite)}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter") onSelect(invite);
              }}
            >
              <td>
                <strong>{invite.submitted_name || invite.name}</strong>
                <small>{invite.household_name}</small>
              </td>
              <td>
                <span>{invite.submitted_email || invite.email || "—"}</span>
                <small>{invite.submitted_phone || invite.phone || "—"}{invite.backup_phone ? ` · Backup: ${invite.backup_phone}` : ""}</small>
              </td>
              <td>
                <span
                  className={`status-pill ${invite.rsvp_status || "pending"}`}
                >
                  {invite.rsvp_status === "yes"
                    ? "Attending"
                    : invite.rsvp_status === "no"
                      ? "Declined"
                      : "Pending"}
                </span>
              </td>
              <td>{invite.attendee_count ?? "—"}</td>
              <td>{invite.adult_count ?? "—"}</td>
              <td>{invite.child_count ?? "—"}</td>
              <td>{invite.jeffism_text ? "✓" : "—"}</td>
              <td>
                {invite.submitted_at
                  ? new Date(invite.submitted_at).toLocaleDateString()
                  : "—"}
              </td>
              <td>
                <a className="table-print-link" href={`/admin/print/${invite.id}`} target="_blank" rel="noreferrer" onClick={(event) => event.stopPropagation()}>
                  <i className="fa-solid fa-print" aria-hidden="true" /> Print
                </a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ImportPanel({ onComplete }: { onComplete: () => void }) {
  const [csv, setCsv] = useState("");
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const fileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) file.text().then(setCsv);
    setRows([]);
  };
  const preview = async () => {
    setBusy(true);
    setError("");
    try {
      const result = await api<{ rows: ImportRow[] }>("/admin/api/import", {
        method: "POST",
        body: JSON.stringify({ csv, confirm: false }),
      });
      setRows(result.rows);
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Could not preview CSV.",
      );
    } finally {
      setBusy(false);
    }
  };
  const confirm = async () => {
    setBusy(true);
    setError("");
    try {
      const result = await api<{ imported: Record<string, unknown>[] }>(
        "/admin/api/import",
        { method: "POST", body: JSON.stringify({ csv, confirm: true }) },
      );
      downloadCsv("generated-invitation-links.csv", result.imported);
      onComplete();
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Could not import CSV.",
      );
    } finally {
      setBusy(false);
    }
  };
  const hasErrors = rows.some((row) => row.errors.length);
  return (
    <section className="import-panel admin-panel">
      <div className="import-instructions">
        <p className="step-number">01</p>
        <h2>Choose your exported Google Sheet</h2>
        <p>
          Required: <code>name</code>. <code>Mobile Phone</code> becomes the primary texting number and <code>Backup Mobile</code> adds a second texting action with the same RSVP link. When a Mobile Phone column exists, the general Phone column is ignored. Other optional columns: household_name, email, max_party_size, notes.
        </p>
        <input type="file" accept=".csv,text/csv" onChange={fileChange} />
        <button
          className="admin-button"
          disabled={!csv || busy}
          onClick={preview}
        >
          {busy ? "Checking…" : "Preview import"}
        </button>
      </div>
      {error && (
        <p className="admin-error" role="alert">
          {error}
        </p>
      )}
      {rows.length > 0 && (
        <div className="import-preview">
          <p className="step-number">02</p>
          <h2>Review {rows.length} invitations</h2>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Row</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Backup</th>
                  <th>Maximum</th>
                  <th>Check</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.rowNumber}
                    className={row.errors.length ? "row-error" : ""}
                  >
                    <td>{row.rowNumber}</td>
                    <td>{row.name || "Missing"}</td>
                    <td>{row.email || "—"}</td>
                    <td>{row.phone || "—"}</td>
                    <td>{row.backupPhone || "—"}</td>
                    <td>{row.maxPartySize || "Default"}</td>
                    <td>
                      {row.errors.length ? row.errors.join(" ") : "Ready"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button
            className="admin-button"
            disabled={hasErrors || busy}
            onClick={confirm}
          >
            {busy ? "Importing…" : `Import ${rows.length} & download links`}
          </button>
          <p className="help">
            The downloaded CSV includes the shared SMS/email invite URL and
            ready-to-copy message fields.
          </p>
        </div>
      )}
    </section>
  );
}

function JeffismPanel({
  invitations,
  onSelect,
}: {
  invitations: Invitation[];
  onSelect: (invite: Invitation) => void;
}) {
  return (
    <section>
      <div className="jeffism-toolbar">
        <p>{invitations.length} contributions for the slideshow archives.</p>
        <a className="admin-button" href="/admin/api/export/jeffisms.csv">
          Export Jeffisms
        </a>
      </div>
      <div className="jeffism-grid">
        {invitations.map((invite) => (
          <article className="jeffism-card" key={invite.id}>
            <button
              className={`favorite ${invite.jeffism_favorite ? "active" : ""}`}
              onClick={() => onSelect(invite)}
              aria-label="Edit favorite status"
            >
              ★
            </button>
            <blockquote>“{invite.jeffism_text}”</blockquote>
            <footer>
              <span>
                {invite.jeffism_anonymous
                  ? "Anonymous in slideshow"
                  : invite.jeffism_credit || invite.name}
              </span>
              <div>
                <button
                  onClick={() =>
                    navigator.clipboard.writeText(invite.jeffism_text || "")
                  }
                >
                  Copy
                </button>
                <button onClick={() => onSelect(invite)}>Edit</button>
              </div>
            </footer>
          </article>
        ))}
      </div>
      {!invitations.length && (
        <p className="empty-table">No Jeffisms yet. Give it time.</p>
      )}
    </section>
  );
}

function EditInvitation({
  invitation,
  onClose,
  onSaved,
}: {
  invitation: Invitation;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    ...invitation,
    rsvp_status: invitation.rsvp_status || "",
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { closing, requestClose } = useAnimatedClose(onClose);
  const set = (key: string, value: unknown) =>
    setForm((current) => ({ ...current, [key]: value }));
  const save = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api(`/admin/api/invitations/${invitation.id}`, {
        method: "PATCH",
        body: JSON.stringify(form),
      });
      requestClose(onSaved);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Update failed.");
    } finally {
      setBusy(false);
    }
  };
  const regenerate = async () => {
    if (!confirm("Invalidate the old invitation link and create a new one?"))
      return;
    setBusy(true);
    try {
      const result = await api<{ token: string }>(
        `/admin/api/invitations/${invitation.id}/regenerate-token`,
        { method: "POST" },
      );
      set("token", result.token);
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Could not regenerate link.",
      );
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    const warning = invitation.submitted_at
      ? `Delete ${invitation.name}? This permanently removes the submitted RSVP and invalidates the invitation link.`
      : `Delete ${invitation.name}? This permanently removes the invitation and invalidates its link.`;
    if (!confirm(warning)) return;
    setBusy(true);
    setError("");
    try {
      await api(`/admin/api/invitations/${invitation.id}`, { method: "DELETE" });
      requestClose(onSaved);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not delete invitation.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      className={`modal-backdrop edit-backdrop ${closing ? "is-closing" : ""}`}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) requestClose();
      }}
    >
      <section
        className="edit-panel"
        role="dialog"
        aria-modal="true"
        aria-label={`Edit ${invitation.name}`}
      >
        <button className="icon-button" onClick={() => requestClose()}>
          ×
        </button>
        <p className="eyebrow">Invitation #{invitation.id}</p>
        <h2>{invitation.name}</h2>
        <form onSubmit={save}>
          <div className="edit-grid">
            <label>
              Name
              <input
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
              />
            </label>
            <label>
              Household
              <input
                value={form.household_name || ""}
                onChange={(e) => set("household_name", e.target.value)}
              />
            </label>
            <label>
              Email
              <input
                type="email"
                value={form.email || ""}
                onChange={(e) => set("email", e.target.value)}
              />
            </label>
            <label>
              Invitation phone
              <input
                value={form.phone || ""}
                onChange={(e) => set("phone", e.target.value)}
              />
              <small className="field-help">Used by Text Invites while this RSVP is pending.</small>
            </label>
            <label>
              Backup mobile
              <input
                value={form.backup_phone || ""}
                onChange={(e) => set("backup_phone", e.target.value)}
              />
              <small className="field-help">Gets a separate text action with this invitation’s same RSVP link.</small>
            </label>
            <label>
              Maximum party size
              <input
                type="number"
                min="1"
                max="20"
                value={form.max_party_size || ""}
                onChange={(e) => set("max_party_size", e.target.value)}
              />
            </label>
            <label>
              Status
              <select
                value={form.rsvp_status}
                onChange={(e) => set("rsvp_status", e.target.value)}
              >
                <option value="">Pending</option>
                <option value="yes">Attending</option>
                <option value="no">Declined</option>
              </select>
            </label>
            <label>
              Party size
              <input
                type="number"
                min="0"
                max="20"
                value={form.attendee_count ?? 0}
                onChange={(e) => set("attendee_count", Number(e.target.value))}
              />
            </label>
            <label>
              Adults
              <input
                type="number"
                min="0"
                max="20"
                value={form.adult_count ?? 0}
                onChange={(e) => set("adult_count", Number(e.target.value))}
              />
            </label>
            <label>
              Kids
              <input
                type="number"
                min="0"
                max="20"
                value={form.child_count ?? 0}
                onChange={(e) => set("child_count", Number(e.target.value))}
              />
            </label>
            {form.rsvp_status && <>
              <label>
                Submitted name
                <input
                  value={form.submitted_name || ""}
                  onChange={(e) => set("submitted_name", e.target.value)}
                />
              </label>
              <label>
                Submitted email
                <input
                  value={form.submitted_email || ""}
                  onChange={(e) => set("submitted_email", e.target.value)}
                />
              </label>
              <label>
                Submitted phone
                <input
                  value={form.submitted_phone || ""}
                  onChange={(e) => set("submitted_phone", e.target.value)}
                />
              </label>
            </>}
          </div>
          <label>
            RSVP note
            <textarea
              rows={2}
              value={form.rsvp_note || ""}
              onChange={(e) => set("rsvp_note", e.target.value)}
            />
          </label>
          <label>
            Jeffism
            <textarea
              rows={4}
              maxLength={280}
              value={form.jeffism_text || ""}
              onChange={(e) => set("jeffism_text", e.target.value)}
            />
          </label>
          <label>
            Jeffism credit
            <input
              value={form.jeffism_credit || ""}
              onChange={(e) => set("jeffism_credit", e.target.value)}
            />
          </label>
          <div className="check-row">
            <label className="checkbox">
              <input
                type="checkbox"
                checked={Boolean(form.jeffism_anonymous)}
                onChange={(e) => set("jeffism_anonymous", e.target.checked)}
              />{" "}
              Anonymous in slideshow
            </label>
            <label className="checkbox">
              <input
                type="checkbox"
                checked={Boolean(form.jeffism_favorite)}
                onChange={(e) => set("jeffism_favorite", e.target.checked)}
              />{" "}
              Favorite
            </label>
          </div>
          <label>
            Admin notes
            <textarea
              rows={3}
              value={form.admin_note || ""}
              onChange={(e) => set("admin_note", e.target.value)}
            />
          </label>
          {error && (
            <p className="admin-error" role="alert">
              {error}
            </p>
          )}
          <div className="edit-actions">
            <div className="danger-actions">
              <a className="print-invite-link" href={`/admin/print/${invitation.id}`} target="_blank" rel="noreferrer">
                <i className="fa-solid fa-print" aria-hidden="true" /> Print invite
              </a>
              <button type="button" className="danger-link" onClick={regenerate} disabled={busy}>
                Regenerate invite link
              </button>
              <button type="button" className="danger-link delete-invitation" onClick={remove} disabled={busy}>
                Delete invitation
              </button>
            </div>
            <button className="admin-button" disabled={busy}>
              {busy ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
